import React from 'react';
import { Box, Button, Typography, LinearProgress, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { AiActions } from '../../../Services/_actions/index.js';
import ModalForms from '../../Shared/Modals/index.js';
import { validateBulkUpload } from '../../Management/validations.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

interface BulkUploadModalProps {
    open: boolean;
    onClose: () => void;
    selectedBoard: string;
    selectedClass: string;
    selectedSubject: string;
    onUploadSuccess?: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
    open,
    onClose,
    selectedBoard,
    selectedClass,
    selectedSubject,
    onUploadSuccess,
}) => {
    const actions = AiActions();
    const { showSnackBar } = useSnackBar();
    const [file, setFile] = React.useState<File | null>(null);
    const [uploading, setUploading] = React.useState(false);
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [success, setSuccess] = React.useState('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const errors = validateBulkUpload({
            file: selected,
            selectedBoard,
            selectedClass,
            selectedSubject,
        });

        if (errors.file) {
            setErrors(errors);
            setFile(null);
            return;
        }

        setErrors({});
        setFile(selected);
    };

    const handleUpload = async () => {
        const errors = validateBulkUpload({
            file,
            selectedBoard,
            selectedClass,
            selectedSubject,
        });

        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }

        try {
            setUploading(true);
            setErrors({});
            setSuccess('');

            const formData = new FormData();
            formData.append('file', file);
            formData.append('board', selectedBoard);
            formData.append('class', selectedClass);
            formData.append('subject', selectedSubject);

            await actions.bulkUploadQuestions(formData);

            showSnackBar('Questions successfully uploaded!', 'success');
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            onUploadSuccess?.();
        } catch (err: any) {
            const backendMessage =
                err?.response?.data?.message || err?.message || 'Upload failed. Please try again.';

            showSnackBar(backendMessage, 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadSample = async () => {
        try {
            await actions.downloadsamplequesionExcel();
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handleClose = () => {
        if (uploading) return;
        setFile(null);
        setErrors({});
        setSuccess('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const modalBody = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Info */}
            <Typography variant="body2" sx={{ color: '#666', fontSize: 13 }}>
                Upload questions using an Excel file. Supported formats: .xlsx, .xls
            </Typography>
            {/* Sample Download */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: 12, color: '#666' }}>
                    Don't know the format?
                </Typography>
                <Button
                    variant="text"
                    size="small"
                    onClick={handleDownloadSample}
                    sx={{ textTransform: 'none', fontSize: 12, p: 0, minWidth: 0 }}
                >
                    Download Sample Excel
                </Button>
            </Box>
            {/* File Input Area */}
            <Box
                sx={{
                    border: '2px dashed #90caf9',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#f0f7ff',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#e3f2fd' },
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <UploadFileIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {file ? file.name : 'Click to select an Excel file'}
                </Typography>
                {!file && (
                    <Typography variant="caption" sx={{ color: '#999' }}>
                        .xlsx, .xls files supported
                    </Typography>
                )}
            </Box>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            {errors.file && (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                    {errors.file}
                </Alert>
            )}
            {errors.board && (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                    {errors.board}
                </Alert>
            )}
            {errors.class && (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                    {errors.class}
                </Alert>
            )}
            {errors.subject && (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                    {errors.subject}
                </Alert>
            )}
            {/* Error / Success Messages */}
            {success && <Alert severity="success" sx={{ fontSize: 12 }}>{success}</Alert>}

            {/* Upload Progress */}
            {uploading && <LinearProgress />}
        </Box>
    );

    const modalFooter = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                disabled={uploading}
                sx={{ textTransform: 'none' }}
            >
                Cancel
            </Button>
            <Button
                variant="contained"
                size="small"
                onClick={handleUpload}
                disabled={!file || uploading}
                sx={{ textTransform: 'none' }}
            >
                {uploading ? 'Uploading...' : 'Upload Questions'}
            </Button>
        </Box>
    );

    return (
        <ModalForms
            show={open}
            handleClose={handleClose}
            modalHeader="Bulk Upload Questions"
            modalBody={modalBody}
            modalFooter={modalFooter}
            size="sm"
        />
    );
};

export default BulkUploadModal;
